import React, { useState, useEffect, useCallback } from 'react';
import {
  Input, Spin, Tooltip, Button, Alert, Dropdown, Menu,
  Modal, Form, Select, Badge, Tag, Space, Typography, message, Upload
} from 'antd';
import {
  SearchOutlined, PlusOutlined, EditOutlined, LeftOutlined, RightOutlined,
  FilterOutlined, FolderOutlined, FolderOpenOutlined, RightOutlined as RightArrow,
  DownOutlined, MoreOutlined, DeleteOutlined
} from '@ant-design/icons';
import LayoutPage from '../../../layouts/Main';
import MarketPlaceService from '../../../utils/api/marketplace';
import '../../../pages/Marketplace/categoryAdmin/categoriesAdmin.scss';

const { Title } = Typography;

const CategoriesAdmin = () => {
  const [categories, setCategories] = useState([]);
  const [hierarchicalCategories, setHierarchicalCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [currentParent, setCurrentParent] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const getBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

  const normalizeUpload = (event) => {
    if (Array.isArray(event)) {
      return event;
    }
    return event?.fileList || [];
  };

  const generateSlug = (name) => {
    if (!name) return '';

    return name
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '');
  };

  const buildHierarchicalStructure = useCallback((categoryList) => {
    if (!categoryList || categoryList.length === 0) {
      setHierarchicalCategories([]);
      return;
    }

    const categoriesMap = new Map();
    const rootCategories = [];

    categoryList.forEach(category => {
      categoriesMap.set(category.slug, {
        ...category,
        children: [],
        hasChildren: false
      });
    });

    categoryList.forEach(category => {
      const categoryNode = categoriesMap.get(category.slug);

      if (category.parent_slug) {
        const parent = categoriesMap.get(category.parent_slug);
        if (parent) {
          parent.children.push(categoryNode);
          parent.hasChildren = true;
        } else {
          rootCategories.push(categoryNode);
        }
      } else {
        rootCategories.push(categoryNode);
      }
    });

    rootCategories.sort((a, b) => a.name.localeCompare(b.name));

    const flattenWithLevels = (nodes, level = 0, result = []) => {
      nodes.forEach(node => {
        result.push({
          ...node,
          level: level,
          children: undefined
        });

        if (expandedRows.has(node.slug)) {
          node.children.sort((a, b) => a.name.localeCompare(b.name));
          flattenWithLevels(node.children, level + 1, result);
        }
      });
      return result;
    };

    const flatList = flattenWithLevels(rootCategories);
    setHierarchicalCategories(flatList);
  }, [expandedRows]);

  useEffect(() => {
    if (searchText.trim() === '') {
      buildHierarchicalStructure(categories);
    } else {
      const filtered = categories.filter(category =>
        category.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        category.slug?.toLowerCase().includes(searchText.toLowerCase()) ||
        category.parent_name?.toLowerCase().includes(searchText.toLowerCase())
      );
      buildHierarchicalStructure(filtered);
    }
  }, [searchText, categories, expandedRows, buildHierarchicalStructure]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await MarketPlaceService.getCategories();

      let categoriesData = [];

      if (response.data && response.data.success && Array.isArray(response.data.categories)) {
        categoriesData = response.data.categories;
      } else if (response.data && Array.isArray(response.data)) {
        categoriesData = response.data;
      } else if (Array.isArray(response)) {
        categoriesData = response;
      } else {
        console.warn('Formato de datos inesperado');
        setCategories([]);
        setHierarchicalCategories([]);
        return;
      }

      const validCategories = categoriesData
        .filter(cat => {
          const isValid = cat && cat.name && cat.slug;
          if (!isValid) {
            console.warn('Categoría inválida encontrada:', cat);
          }
          return isValid;
        })
        .map(cat => {
          const parentCategory = categoriesData.find(parent =>
            parent.slug === cat.parent_slug && parent.name && parent.slug
          );

          return {
            id: cat.id,
            name: cat.name,
            slug: cat.slug,
            parent_slug: cat.parent_slug || null,
            parent_name: parentCategory ? parentCategory.name : null,
            level: 0,
            path: cat.path || cat.slug,
            created_at: cat.created_at,
            hasChildren: false
          };
        });

      setCategories(validCategories);

    } catch (error) {
      console.error('Error al cargar las categorías:', error);
      setCategories([]);
      setHierarchicalCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (slug) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(slug)) {
      newExpanded.delete(slug);
    } else {
      newExpanded.add(slug);
    }
    setExpandedRows(newExpanded);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    editForm.setFieldsValue({
      name: category.name,
      slug: category.slug,
      parent_slug: category.parent_slug || null
    });
    setEditModalVisible(true);
  };

  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;

    try {
      setDeleteLoading(true);
      const response = await MarketPlaceService.deleteCategoryBySlug(categoryToDelete.slug);

      if (response.data && response.data.success) {
        message.success(`Categoría "${categoryToDelete.name}" eliminada exitosamente. ${response.data.msg || ''}`);
        await loadCategories();
        setDeleteModalVisible(false);
        setCategoryToDelete(null);
      } else {
        message.error('Error al eliminar la categoría');
      }
    } catch (error) {
      console.error('Error al eliminar la categoría:', error);
      message.error('Error al eliminar la categoría');
    } finally {
      setDeleteLoading(false);
    }
  };

  const showDeleteConfirm = (category) => {
    const childCount = categories.filter(cat => cat.parent_slug === category.slug).length;

    const modalContent = (
      <div>
        <p>¿Estás seguro de que deseas eliminar la categoría <strong>"{category.name}"</strong>?</p>

        {childCount > 0 && (
          <Alert
            message={`Esta categoría tiene ${childCount} subcategoría${childCount > 1 ? 's' : ''} que también serán eliminadas.`}
            type="warning"
            showIcon
            style={{ marginTop: 10 }}
          />
        )}

        <Alert
          message="Esta acción eliminará permanentemente la categoría y todas sus subcategorías. Los productos asociados perderán esta categoría."
          type="error"
          showIcon
          style={{ marginTop: 10 }}
        />
      </div>
    );

    Modal.confirm({
      title: 'Confirmar eliminación',
      content: modalContent,
      okText: 'Sí, eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          setDeleteLoading(true);
          const response = await MarketPlaceService.deleteCategoryBySlug(category.slug);

          if (response.data && response.data.success) {
            message.success(`Categoría "${category.name}" eliminada exitosamente. ${response.data.msg || ''}`);
            await loadCategories();
          } else {
            message.error('Error al eliminar la categoría');
          }
        } catch (error) {
          console.error('Error al eliminar la categoría:', error);
          message.error('Error al eliminar la categoría');
        } finally {
          setDeleteLoading(false);
        }
      },
      onCancel() { },
    });
  };

  const handleUpdateCategory = async (values) => {
    try {
      setLoading(true);

      let slug = values.slug;
      if (values.name !== editingCategory.name) {
        slug = generateSlug(values.name);
      }

      const updateData = {};
      if (values.name !== editingCategory.name) updateData.name = values.name;
      if (slug !== editingCategory.slug) updateData.slug = slug;
      if (values.parent_slug !== editingCategory.parent_slug) updateData.parent_slug = values.parent_slug;

      const imageFile = values.carousel_image?.[0]?.originFileObj;
      const hasImageUpload = Boolean(imageFile);
      if (imageFile) {
        try {
          updateData.imageBase64 = await getBase64(imageFile);
        } catch (error) {
          message.error('No se pudo procesar la imagen para el carrusel');
          setLoading(false);
          return;
        }
      }

      if (Object.keys(updateData).length === 0) {
        console.warn('No se realizaron cambios');
        setLoading(false);
        return;
      }

      const result = await MarketPlaceService.updateCategory(editingCategory.id, updateData);

      if (result.data && result.data.success) {
        console.log('Categoría actualizada correctamente');
        await loadCategories();
        setEditModalVisible(false);
        setEditingCategory(null);
        editForm.resetFields();
        if (hasImageUpload) {
          message.success('Imagen del carrusel actualizada');
          setTimeout(() => window.location.reload(), 600);
        }
      } else {
        console.error('Error al actualizar la categoría');
      }

    } catch (error) {
      console.error('Error al actualizar la categoría:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async (values) => {
    try {
      setLoading(true);
      const parentSlug = values.parent_slug || currentParent?.slug || null;

      const slug = generateSlug(values.name);

      const categoryData = {
        name: values.name,
        slug: slug,
        parent_slug: parentSlug
      };

      const imageFile = values.carousel_image?.[0]?.originFileObj;
      const hasImageUpload = Boolean(imageFile);
      if (imageFile) {
        try {
          categoryData.imageBase64 = await getBase64(imageFile);
        } catch (error) {
          message.error('No se pudo procesar la imagen para el carrusel');
          setLoading(false);
          return;
        }
      }
      if (parentSlug) {
        const parentCategory = categories.find(cat => cat.slug === parentSlug);
        categoryData.path = parentCategory ? `${parentCategory.path}/${slug}` : slug;
      } else {
        categoryData.path = slug;
      }

      const result = await MarketPlaceService.createCategory(categoryData);
      const isSuccess = (result.data && result.data.success) ||
        (result.success) ||
        (result.data && result.data.id);

      if (isSuccess) {
        console.log('Categoría creada exitosamente');
        await loadCategories();

        if (parentSlug) {
          const newExpanded = new Set(expandedRows);
          newExpanded.add(parentSlug);
          setExpandedRows(newExpanded);
        }

        setModalVisible(false);
        form.resetFields();
        setCurrentParent(null);
        if (hasImageUpload) {
          message.success('Imagen del carrusel subida');
          setTimeout(() => window.location.reload(), 600);
        }
      } else {
        console.error('Error al guardar la categoría');
      }

    } catch (error) {
      console.error('Error al guardar la categoría:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setCurrentParent(null);
    form.resetFields();
  };

  const handleEditModalCancel = () => {
    setEditModalVisible(false);
    setEditingCategory(null);
    editForm.resetFields();
  };

  const openNewCategoryModal = () => {
    setCurrentParent(null);
    form.setFieldsValue({
      parent_slug: null,
      name: '',
      slug: ''
    });
    setModalVisible(true);
  };

  const openNewSubcategoryModal = (parentCategory) => {
    setCurrentParent(parentCategory);
    form.setFieldsValue({
      parent_slug: parentCategory.slug,
      name: '',
      slug: ''
    });
    setModalVisible(true);
  };

  const handleNameChange = (e, isEditForm = false) => {
    const name = e.target.value;
    const slug = generateSlug(name);

    if (isEditForm) {
      editForm.setFieldsValue({ slug });
    } else {
      form.setFieldsValue({ slug });
    }
  };

  const getLevelTag = (level) => {
    const types = {
      0: { color: 'blue', text: 'Principal' },
      1: { color: 'cyan', text: 'Sub' },
      2: { color: 'geekblue', text: 'N2' },
    };
    const type = types[level] || { color: 'default', text: `N${level + 1}` };
    return <Tag color={type.color} size="small">{type.text}</Tag>;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    try {
      if (timestamp._seconds) {
        const date = new Date(timestamp._seconds * 1000);
        return date.toLocaleDateString('es-ES');
      } else if (typeof timestamp === 'string') {
        const date = new Date(timestamp);
        return date.toLocaleDateString('es-ES');
      } else {
        return '-';
      }
    } catch (error) {
      return '-';
    }
  };

  const getModalTitle = () => {
    if (currentParent) {
      return `Nueva Subcategoría en ${currentParent.name}`;
    }
    return 'Nueva Categoría Principal';
  };

  const getEditModalTitle = () => {
    return `Editar Categoría: ${editingCategory?.name || ''}`;
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleClearSearch = () => {
    setSearchText('');
  };

  const handleClearAllFilters = () => {
    setSearchText('');
    setExpandedRows(new Set());
  };

  const hasActiveFilters = searchText.trim() !== '';

  const getDisplayRange = () => {
    const start = ((currentPage - 1) * pageSize) + 1;
    const end = Math.min(start + hierarchicalCategories.length - 1, hierarchicalCategories.length);
    return { start, end };
  };

  const displayRange = getDisplayRange();

  const getLevelClass = (level) => {
    const levelMap = {
      0: "level-badge principal",
      1: "level-badge subcategoria",
      2: "level-badge nivel2",
      3: "level-badge nivel3"
    };
    return levelMap[level] || "level-badge";
  };

  const levelMenu = (
    <Menu>
      <Menu.Item key="all-levels" onClick={() => { }}>
        Todos los niveles
      </Menu.Item>
      <Menu.Item key="principal" onClick={() => { }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div className="level-indicator principal" />
          <span>Principal</span>
        </div>
      </Menu.Item>
      <Menu.Item key="subcategoria" onClick={() => { }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div className="level-indicator subcategoria" />
          <span>Subcategoría</span>
        </div>
      </Menu.Item>
    </Menu>
  );

  if (loading && categories.length === 0) {
    return (
      <div className="admin-categories-container">
        <div className="orders-card">
          <div className="loading-container">
            <Spin size="large" />
            <p>Cargando categorías...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <LayoutPage>
      <div className="admin-categories-container">
        <div className="orders-card">
          <div className="table-header-section">
            <h1>Administrador de Categorías</h1>

            <div className="filters-section">
              <div className="search-filter">
                <Input
                  className="simple-search"
                  placeholder="Buscar categorías por nombre, slug o padre..."
                  value={searchText}
                  onChange={handleSearchChange}
                  onKeyPress={handleSearchKeyPress}
                  prefix={<SearchOutlined />}
                  size="large"
                  allowClear
                  onClear={handleClearSearch}
                  suffix={
                    <Button
                      type="link"
                      size="small"
                      onClick={() => { }}
                      loading={loading}
                      disabled={searchText.trim() === ""}
                    >
                      Buscar
                    </Button>
                  }
                  disabled={loading}
                />
              </div>

              <div className="add-user-filter">
                <button className="add-user-button" onClick={openNewCategoryModal} disabled={loading}>
                  <PlusOutlined style={{ marginRight: 8 }} />
                  Agregar categoría
                </button>
              </div>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="active-filters-section">
              <span className="filters-label">
                Filtros activos:
              </span>
              <div className="filters-tags">
                {searchText.trim() !== "" && (
                  <span className="filter-tag search-tag">
                    Búsqueda: "{searchText}"
                  </span>
                )}
              </div>
              <Button
                type="link"
                size="small"
                onClick={handleClearAllFilters}
                className="clear-filters-button"
              >
                Limpiar todos los filtros
              </Button>
            </div>
          )}

          <Alert
            message="Tip: Haz clic en las flechas para expandir/colapsar subcategorías"
            type="info"
            showIcon
            className="info-alert"
            closable
          />

          <div className="table-container">
            <div className="styled-table custom-table">
              <div className="table-header">
                <div className="header-row">
                  <div className="header-cell categoria">Categoría</div>
                  <div className="header-cell nivel">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        Nivel
                      </div>
                    </div>
                  </div>
                  <div className="header-cell creado">Creado</div>
                  <div className="header-cell accion">Acciones</div>
                </div>
              </div>

              <div className="table-body">
                {hierarchicalCategories.length === 0 ? (
                  <div className="empty-state">
                    {loading ? (
                      <Spin size="small" />
                    ) : hasActiveFilters ? (
                      <div>
                        <p>No se encontraron categorías con los filtros aplicados</p>
                        <Button
                          type="link"
                          onClick={handleClearAllFilters}
                          className="show-all-button"
                        >
                          Mostrar todas las categorías
                        </Button>
                      </div>
                    ) : (
                      <div className="empty-content">
                        <FolderOutlined className="empty-icon" />
                        <p>No hay categorías disponibles</p>
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={openNewCategoryModal}
                          className="create-first-button"
                        >
                          Crear primera categoría
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  hierarchicalCategories.map(category => {
                    const indentSize = category.level * 24;

                    return (
                      <div key={category.slug} className="table-row">
                        <div className="body-row">
                          <div className="body-cell categoria" data-label="Categoría">
                            <div style={{
                              paddingLeft: indentSize,
                              display: 'flex',
                              alignItems: 'center',
                              flex: 1
                            }}>
                              {category.hasChildren ? (
                                <button
                                  type="button"
                                  className="expand-button"
                                  onClick={() => toggleExpand(category.slug)}
                                >
                                  {expandedRows.has(category.slug) ?
                                    <DownOutlined className="expand-icon" /> :
                                    <RightArrow className="expand-icon" />}
                                </button>
                              ) : (
                                <div style={{ width: 32, marginRight: 8 }} />
                              )}
                              {category.level === 0 ? (
                                <FolderOpenOutlined className="folder-icon principal" />
                              ) : (
                                <FolderOutlined className="folder-icon sub" />
                              )}
                              <span className={`category-name ${category.level === 0 ? 'principal' : 'sub'}`}>
                                {category.name}
                              </span>
                            </div>
                          </div>
                          <div className="body-cell nivel" data-label="Nivel">
                            <span className={getLevelClass(category.level)}>
                              {category.level === 0 ? 'Principal' :
                                category.level === 1 ? 'Subcategoría' :
                                  `Nivel ${category.level + 1}`}
                            </span>
                          </div>
                          <div className="body-cell creado" data-label="Creado">
                            <span className="date-text">
                              {formatDate(category.created_at)}
                            </span>
                          </div>
                          <div className="body-cell accion" data-label="Acciones">
                            <Tooltip title="Editar categoría" placement="left">
                              <button
                                className="edit-icon-button"
                                onClick={() => handleEditCategory(category)}
                                disabled={loading}
                              >
                                <EditOutlined />
                              </button>
                            </Tooltip>
                            <Dropdown
                              overlay={
                                <Menu>
                                  <Menu.Item
                                    key="subcategory"
                                    icon={<PlusOutlined />}
                                    onClick={() => openNewSubcategoryModal(category)}
                                  >
                                    Nueva subcategoría
                                  </Menu.Item>
                                  <Menu.Divider />
                                  <Menu.Item
                                    key="delete"
                                    icon={<DeleteOutlined />}
                                    danger
                                    onClick={() => showDeleteConfirm(category)}
                                    disabled={deleteLoading}
                                  >
                                    Eliminar
                                  </Menu.Item>
                                </Menu>
                              }
                              trigger={['click']}
                            >
                              <button className="more-button">
                                <MoreOutlined />
                              </button>
                            </Dropdown>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {hierarchicalCategories.length > 0 && (
            <div className="main-pagination-container">
              <div className="pagination-info">
                <span>
                  {hasActiveFilters
                    ? `Mostrando ${hierarchicalCategories.length} categoría${hierarchicalCategories.length !== 1 ? 's' : ''} filtrada${hierarchicalCategories.length !== 1 ? 's' : ''}`
                    : `Mostrando categorías ${displayRange.start}-${displayRange.end} de ${hierarchicalCategories.length}`
                  }
                </span>
              </div>

              <div className="page-size-selector">
                <span>Mostrar:</span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  disabled={loading}
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span>por página</span>
              </div>

              <div className="simple-pagination">
                <Button
                  icon={<LeftOutlined />}
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1 || loading}
                  loading={loading}
                  className="pagination-button"
                >
                  Anterior
                </Button>

                <div className="current-page-indicator">
                  Página {currentPage}
                </div>

                <Button
                  icon={<RightOutlined />}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  iconPosition="end"
                  disabled={currentPage * pageSize >= hierarchicalCategories.length || loading}
                  loading={loading}
                  className="pagination-button"
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}

          {hasActiveFilters && hierarchicalCategories.length > 0 && (
            <div className="search-clear-section">
              <Button
                type="link"
                onClick={handleClearAllFilters}
                disabled={loading}
                className="show-all-link"
              >
                Mostrar todas las categorías
              </Button>
            </div>
          )}
        </div>

        <Modal
          title={getModalTitle()}
          open={modalVisible}
          onCancel={handleModalCancel}
          footer={null}
          width={600}
          destroyOnClose
          className="custom-modal"
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleCreateCategory}
          >
            {currentParent && (
              <Alert
                message={`Creando subcategoría en: ${currentParent.name}`}
                description={`Esta categoría será hija de "${currentParent.name}"`}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            <Form.Item
              name="name"
              label="Nombre de la categoría"
              rules={[
                { required: true, message: 'Ingresa el nombre de la categoría' },
                { min: 2, message: 'Mínimo 2 caracteres' }
              ]}
            >
              <Input
                placeholder="Ej: Productos de limpieza"
                onChange={(e) => handleNameChange(e, false)}
              />
            </Form.Item>

            <Form.Item
              name="slug"
              label="Slug (URL) - Generado automáticamente"
            >
              <Input
                placeholder="Se generará automáticamente"
                readOnly
              />
            </Form.Item>

            <Form.Item name="parent_slug" hidden>
              <Input />
            </Form.Item>

            <Form.Item
              name="carousel_image"
              label="Imagen para carrusel (opcional)"
              valuePropName="fileList"
              getValueFromEvent={normalizeUpload}
              extra="Recomendado: 1312 x 440 px (ratio 2.98:1). Alternativa ligera: 984 x 330 px."
            >
              <Upload
                accept="image/*"
                listType="picture"
                maxCount={1}
                beforeUpload={() => false}
              >
                <Button icon={<PlusOutlined />}>Subir imagen</Button>
              </Upload>
            </Form.Item>

            <Form.Item style={{ textAlign: 'right', marginBottom: 0, marginTop: 24 }}>
              <Space>
                <Button onClick={handleModalCancel} disabled={loading}>
                  Cancelar
                </Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Crear
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title={getEditModalTitle()}
          open={editModalVisible}
          onCancel={handleEditModalCancel}
          footer={null}
          width={600}
          destroyOnClose
          className="custom-modal"
        >
          <Form
            form={editForm}
            layout="vertical"
            onFinish={handleUpdateCategory}
          >
            <Form.Item
              name="name"
              label="Nombre de la categoría"
              rules={[
                { required: true, message: 'Ingresa el nombre de la categoría' },
                { min: 2, message: 'Mínimo 2 caracteres' }
              ]}
            >
              <Input
                placeholder="Ej: Productos de limpieza"
                onChange={(e) => handleNameChange(e, true)}
              />
            </Form.Item>

            <Form.Item
              name="slug"
              label="Slug (URL) - Generado automáticamente"
            >
              <Input
                placeholder="Se generará automáticamente"
                readOnly
              />
            </Form.Item>

            <Form.Item
              name="parent_slug"
              label="Categoría Padre (opcional)"
            >
              <Select
                placeholder="Selecciona una categoría padre"
                allowClear
                options={categories
                  .filter(cat => cat.slug !== editingCategory?.slug)
                  .map(cat => ({
                    value: cat.slug,
                    label: `${cat.name} (${cat.slug})`
                  }))}
              />
            </Form.Item>

            <Form.Item
              name="carousel_image"
              label="Imagen para carrusel (opcional)"
              valuePropName="fileList"
              getValueFromEvent={normalizeUpload}
              extra="Recomendado: 1312 x 440 px (ratio 2.98:1). Alternativa ligera: 984 x 330 px."
            >
              <Upload
                accept="image/*"
                listType="picture"
                maxCount={1}
                beforeUpload={() => false}
              >
                <Button icon={<PlusOutlined />}>Subir imagen</Button>
              </Upload>
            </Form.Item>

            <Form.Item style={{
              textAlign: 'right',
              marginBottom: 0,
              marginTop: 24
            }}>
              <Space>
                <Button
                  onClick={handleEditModalCancel}
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                >
                  Actualizar
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </LayoutPage>
  );
};

export default CategoriesAdmin;